/**
 * The two ways to set up, side by side (migration 070): by hand in the portal,
 * or with Claude Code in the website's repository. Shown on the first screen
 * a new partner sees (Trades & area) and on the Install step. The choice is a
 * per-browser convenience; both paths stay available and the manual one is
 * never hidden behind the agent one.
 */
import { useId, type ReactNode } from 'react';
import { useLocalState } from '../ui/hooks';
import { AgentHandoff } from './AgentHandoff';

export type SetupPathChoice = 'manual' | 'agent';
export const SETUP_PATH_KEY = 'finderly.partner.setup-path';

export function useSetupPath(): [SetupPathChoice, (v: SetupPathChoice) => void] {
  return useLocalState<SetupPathChoice>(SETUP_PATH_KEY, 'manual');
}

/**
 * One pair of labels for the two paths, on every screen that offers them
 * (Trades & area and Install used to say "Set it up here, step by step" and
 * "Paste the snippet yourself" for the same choice; second audit, 2026-09-27).
 * Each screen still describes what the manual path means there.
 */
export const MANUAL_TITLE = 'Set it up yourself';
export const AGENT_TITLE = 'Set up with Claude Code';

export function SetupPathChooser({
  manualDescription,
  manual,
  agentExtra,
}: {
  manualDescription: string;
  /** Rendered when the manual path is chosen; omit to keep manual content outside. */
  manual?: ReactNode;
  /** Secondary content under the one-paste command on the Claude Code path. */
  agentExtra?: ReactNode;
}) {
  const [choice, setChoice] = useSetupPath();
  const name = useId();
  return (
    <div className="stack">
      <fieldset className="path-choice" style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className="visually-hidden">How do you want to set up?</legend>
        <label className="choice" htmlFor={`${name}-manual`}>
          <input
            id={`${name}-manual`}
            type="radio"
            name={name}
            checked={choice === 'manual'}
            onChange={() => setChoice('manual')}
            aria-labelledby={`${name}-manual-t`}
            aria-describedby={`${name}-manual-d`}
          />
          <span className="dot" aria-hidden="true" />
          <span>
            <span className="t" id={`${name}-manual-t`}>
              {MANUAL_TITLE}
            </span>
            <span className="d" id={`${name}-manual-d`} style={{ display: 'block' }}>
              {manualDescription}
            </span>
          </span>
        </label>
        <label className="choice" htmlFor={`${name}-agent`}>
          <input
            id={`${name}-agent`}
            type="radio"
            name={name}
            checked={choice === 'agent'}
            onChange={() => setChoice('agent')}
            aria-labelledby={`${name}-agent-t`}
            aria-describedby={`${name}-agent-d`}
          />
          <span className="dot" aria-hidden="true" />
          <span>
            <span className="t">
              <span id={`${name}-agent-t`}>{AGENT_TITLE}</span> <span className="tag accent">One paste</span>
            </span>
            <span className="d" id={`${name}-agent-d`} style={{ display: 'block' }}>
              Your developer pastes one command in the website’s repo. The agent installs, verifies, brands and tests; you approve anything
              legal or that sends homeowner data.
            </span>
          </span>
        </label>
      </fieldset>
      {choice === 'agent' ? (
        <>
          <AgentHandoff compact />
          {agentExtra ?? null}
        </>
      ) : (
        manual ?? null
      )}
    </div>
  );
}
